/**
 * WP-style configurator form: gate data + contact fields + GDPR.
 * Used in Tester tab (calculate only) and optionally in embed (submit to public API).
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiUrl } from "@/lib/api-client";
import { parseSkSidlo } from "@shared/parse-sk-sidlo";
import { normalizeIcDphValue } from "@shared/normalize-ic-dph";
import { normalizeForPath, normalizeColorCodeForPath } from "./ConfiguratorPreview";
import { DEFAULT_SWATCH_PLACEHOLDER_HEX } from "./price-swatches-config";

const MOTIV_OPTIONS = [
  { value: "m", label: "M" },
  { value: "l", label: "L" },
];
const SURFACE_OPTIONS = [
  { value: "woodgrain", label: "Woodgrain" },
  { value: "planar", label: "Planar" },
  { value: "decocolor", label: "Decocolor" },
  { value: "slategrain", label: "Slategrain" },
  { value: "micrograin", label: "Micrograin" },
  { value: "metallic", label: "Metallic" },
];
/** Fallback ha nincs configuratorProductOptions: mely povrch-ök választhatók motiv szerint */
const SURFACES_BY_MOTIV_FALLBACK: Record<string, string[]> = {
  m: ["woodgrain", "planar", "decocolor"],
  l: ["planar"],
};

/** Ország select: ugyanaz a tipográfia és keret, mint az `Input` (md:text-sm, px-4, rounded-lg). */
const CONFIGURATOR_SELECT_FIELD_CLASS =
  "flex h-12 w-full rounded-lg border border-input bg-background px-4 py-2 font-sans text-base font-normal leading-normal md:text-sm ring-offset-background transition-all duration-200 hover:border-input/80 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input disabled:cursor-not-allowed disabled:opacity-50";

/** Üzenet textarea: megegyezik az inputok szövegméretével és stílusával. */
const CONFIGURATOR_TEXTAREA_FIELD_CLASS =
  "flex min-h-[80px] w-full rounded-lg border border-input bg-background px-4 py-2 font-sans text-base font-normal leading-normal md:text-sm ring-offset-background placeholder:text-muted-foreground transition-all duration-200 hover:border-input/80 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input disabled:cursor-not-allowed disabled:opacity-50";

export interface PriceCombination {
  motiv: string;
  prelis: string;
}

function CheckBadge({ overlay }: { overlay?: boolean }) {
  /* overlay: pipa a swatch fölött (absolute), nem plusz sor – nincs layout shift */
  if (!overlay) {
    return (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--configurator-accent))] text-white text-[11px] font-bold shadow-md shrink-0">
        ✓
      </span>
    );
  }
  return (
    <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--configurator-accent))] text-white text-[11px] font-bold shadow-md z-20 pointer-events-none">
      ✓
    </span>
  );
}

function MotivThumbnail({
  value,
  selected,
  onSelect,
}: {
  value: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const [useJpg, setUseJpg] = useState(false);
  useEffect(() => {
    setUseJpg(false);
  }, [value]);
  const src = useJpg
    ? `/configurator/base-${value}.jpg`
    : `/configurator/base-${value}.png`;
  /* Referencia: nincs felirat a kis képek alatt; a kiválasztott érték a szekciócímkében jelenik meg (Motív brány Prelis L) */
  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onClick={onSelect}
        className={`configurator-swatch relative shadow-sm ${
          selected ? "selected" : ""
        }`}
        style={{ width: 100, height: 75 }}
      >
        <span className="swatch-content">
          <img
            src={src}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setUseJpg(true)}
          />
        </span>
        {selected && <CheckBadge overlay />}
      </button>
    </div>
  );
}

function SurfaceThumbnail({
  overlaySrc,
  fallbackHex,
  selected,
  onSelect,
}: {
  overlaySrc: string | null;
  fallbackHex: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const [imgError, setImgError] = useState(false);
  const showImg = overlaySrc && !imgError;
  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onClick={onSelect}
        className={`configurator-swatch relative shadow-sm ${
          selected ? "selected" : ""
        }`}
        style={{ width: 100, height: 75 }}
      >
        <span className="swatch-content">
          {showImg ? (
            <img
              src={overlaySrc}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <span className="absolute inset-0 block" style={{ backgroundColor: fallbackHex }} />
          )}
        </span>
        {selected && <CheckBadge overlay />}
      </button>
    </div>
  );
}

function ColorSwatch({ overlaySrc, fallbackHex }: { overlaySrc: string | null; fallbackHex: string }) {
  const [imgError, setImgError] = useState(false);
  const showImg = overlaySrc && !imgError;
  return (
    <span className="absolute inset-0 block">
      {!showImg && (
        <span className="absolute inset-0 block" style={{ backgroundColor: fallbackHex }} />
      )}
      {showImg && (
        <img
          src={overlaySrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      )}
    </span>
  );
}

export interface ConfiguratorFormValues {
  width: number;
  height: number;
  motiv: string;
  surface: string;
  color: string;
  electricDrive: boolean;
  address: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  country: string;
  message: string;
  gdprConsent: boolean;
  /** Company quote: optional ico, dic, icDph when chcemNaFirmu */
  ico?: string;
  dic?: string;
  icDph?: string;
  chcemNaFirmu?: boolean;
  /** After successful FinStat lookup (embed); admin calculate uses for 0% preview. */
  finstatLookupSuccess?: boolean;
  /** Obchodné meno (cég neve); a kapcsolattartó a `name` mező. */
  obchodneMeno?: string;
}

function buildConfiguratorFormValues(args: {
  width: string;
  height: string;
  motiv: string;
  surface: string;
  color: string;
  electricDrive: boolean;
  address: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  country: string;
  message: string;
  gdprConsent: boolean;
  chcemNaFirmu: boolean;
  finstatLookupSuccess: boolean;
  ico: string;
  dic: string;
  icDph: string;
  obchodneMeno: string;
}): ConfiguratorFormValues | null {
  const w = Number(args.width);
  const h = Number(args.height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  const values: ConfiguratorFormValues = {
    width: w,
    height: h,
    motiv: args.motiv,
    surface: args.surface,
    color: args.color.trim(),
    electricDrive: args.electricDrive,
    address: args.address.trim(),
    name: args.name.trim(),
    email: args.email.trim(),
    phone: args.phone.trim(),
    city: args.city.trim(),
    country: args.country.trim(),
    message: args.message.trim(),
    gdprConsent: args.gdprConsent,
    chcemNaFirmu: args.chcemNaFirmu,
    finstatLookupSuccess: args.finstatLookupSuccess,
    ...(args.chcemNaFirmu && args.ico.trim()
      ? {
          ico: args.ico.trim(),
          dic: args.dic.trim() || undefined,
          icDph: args.icDph.trim() || undefined,
          ...(args.obchodneMeno.trim() ? { obchodneMeno: args.obchodneMeno.trim() } : {}),
        }
      : {}),
  };
  return values;
}

export interface ConfiguratorProductOption {
  surface: string;
  label?: string;
  motivs: string[];
  colors: { code: string; name: string; colorHex?: string; overlayFilename?: string }[];
}

export interface ConfiguratorFormProps {
  /** When user clicks calculate: call with values; parent runs API and can show result. */
  onCalculate?: (values: ConfiguratorFormValues) => Promise<void>;
  /** Price Generator Tester: log note + run AI classification (deal_ai_classifications). */
  onRecordAiClassification?: (values: ConfiguratorFormValues) => Promise<void>;
  /** Embed (WP iframe): submit to public API; token passed from URL or embedToken prop. */
  onSubmitToPublic?: (values: ConfiguratorFormValues, token: string) => Promise<{ dealId?: string; error?: string } | void>;
  /** If true, show only gate + contact fields and primary CTA (for embed). */
  embedMode?: boolean;
  /** Token for embed submit (WP iframe). When set, used instead of reading from URL so submit always has a valid token. */
  embedToken?: string;
  /** From settings or GET /api/public/configurator/options. If set, dropdowns use this; else fallback. */
  configuratorProductOptions?: ConfiguratorProductOption[];
  /** Ceny alapján: motiv+prelis párok a tpt_*_prices táblákból. Ha megvan, a függőségek innen származnak. */
  priceCombinations?: PriceCombination[];
  /** Admin-defined motiv display order (e.g. ["m","l"]). When set, used instead of hardcoded order. */
  configuratorMotivOrder?: string[];
  /** Controlled (when inside ConfiguratorLayout): sync with preview. */
  motiv?: string;
  surface?: string;
  color?: string;
  onMotivChange?: (v: string) => void;
  onSurfaceChange?: (v: string) => void;
  onColorChange?: (v: string) => void;
}

const defaultValues: ConfiguratorFormValues = {
  width: 2500,
  height: 2250,
  motiv: "m",
  surface: "woodgrain",
  color: "",
  electricDrive: true,
  address: "",
  name: "",
  email: "",
  phone: "",
  city: "",
  country: "Slovensko",
  message: "",
  gdprConsent: false,
};

export default function ConfiguratorForm({
  onCalculate,
  onRecordAiClassification,
  onSubmitToPublic,
  embedMode = false,
  embedToken,
  configuratorProductOptions = [],
  priceCombinations = [],
  configuratorMotivOrder,
  motiv: controlledMotiv,
  surface: controlledSurface,
  color: controlledColor,
  onMotivChange,
  onSurfaceChange,
  onColorChange,
}: ConfiguratorFormProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [recordingAi, setRecordingAi] = useState(false);
  // Width/height: only from user input; never update from API/result (e.g. matchedWidth/matchedHeight).
  const [width, setWidth] = useState(String(defaultValues.width));
  const [height, setHeight] = useState(String(defaultValues.height));
  const [internalMotiv, setInternalMotiv] = useState(defaultValues.motiv);
  const [internalSurface, setInternalSurface] = useState(defaultValues.surface);
  const [internalColor, setInternalColor] = useState(defaultValues.color);

  const motiv = controlledMotiv ?? internalMotiv;
  const surface = controlledSurface ?? internalSurface;
  const color = controlledColor ?? internalColor;
  const setMotivValue = onMotivChange ?? setInternalMotiv;
  const setSurfaceValue = onSurfaceChange ?? setInternalSurface;
  const setColorValue = onColorChange ?? setInternalColor;

  const surfaceOptions =
    configuratorProductOptions.length > 0
      ? configuratorProductOptions
          .filter((o) => o.motivs.includes(motiv))
          .map((o) => ({ surface: o.surface, value: o.surface, label: o.label || o.surface }))
      : (SURFACES_BY_MOTIV_FALLBACK[motiv] ?? ["planar"]).map((value) => ({
          surface: value,
          value,
          label: SURFACE_OPTIONS.find((s) => s.value === value)?.label ?? value,
        }));
  const currentOption = configuratorProductOptions.find(
    (o) => o.surface === surface && o.motivs.includes(motiv)
  );
  const motivOptions =
    configuratorMotivOrder && configuratorMotivOrder.length > 0
      ? configuratorMotivOrder.map((m) => ({ value: m, label: m.toUpperCase() }))
      : configuratorProductOptions.length > 0
        ? (() => {
            const allMotivs = [...new Set(configuratorProductOptions.flatMap((o) => o.motivs))];
            allMotivs.sort((a, b) => (a === "m" ? -1 : b === "m" ? 1 : 0));
            return allMotivs.map((m) => ({ value: m, label: m.toUpperCase() }));
          })()
        : MOTIV_OPTIONS;
  const colorOptions = currentOption?.colors ?? [];
  const [electricDrive, setElectricDrive] = useState(defaultValues.electricDrive);
  const [address, setAddress] = useState(defaultValues.address);
  const [name, setName] = useState(defaultValues.name);
  const [email, setEmail] = useState(defaultValues.email);
  const [phone, setPhone] = useState(defaultValues.phone);
  const [city, setCity] = useState(defaultValues.city);
  const [country, setCountry] = useState(defaultValues.country);
  const [message, setMessage] = useState(defaultValues.message);
  const [gdprConsent, setGdprConsent] = useState(defaultValues.gdprConsent);
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [ico, setIco] = useState("");
  const [obchodneMeno, setObchodneMeno] = useState("");
  const [dic, setDic] = useState("");
  const [icDph, setIcDph] = useState("");
  const [chcemNaFirmu, setChcemNaFirmu] = useState(false);
  const [finstatLookupSuccess, setFinstatLookupSuccess] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [autoLookupBusy, setAutoLookupBusy] = useState(false);
  const [companyLoadError, setCompanyLoadError] = useState<string | null>(null);

  const requireContactFields = !!onSubmitToPublic;
  const showCompanyBlock = !!(onSubmitToPublic || onCalculate);
  const hasCalculateOnly = Boolean(onCalculate) && !onSubmitToPublic;
  /** Úspešné načítanie cez tlačidlo (embed) alebo tester s ručným doplnením. */
  const showCompanyDetailFields = chcemNaFirmu && (finstatLookupSuccess || hasCalculateOnly);
  /** Po úspešnom načítaní sú firemné polia len na čítanie. */
  const companyFieldsLocked = chcemNaFirmu && finstatLookupSuccess;
  const finstatLockedInputClass =
    "bg-muted/50 border-muted/80 cursor-not-allowed opacity-90";

  const contactCardClass =
    "rounded-md border border-border/60 bg-muted/20 p-4 space-y-4";

  const applyFinstatPayload = useCallback((data: {
    meno_firmy?: string;
    sidlo_firmy?: string;
    dic?: string;
    ic_dph?: string;
  }) => {
    if (data.meno_firmy) setObchodneMeno(data.meno_firmy);
    if (data.sidlo_firmy) {
      const { street, city: parsedCity } = parseSkSidlo(String(data.sidlo_firmy));
      setAddress(street);
      setCity(parsedCity);
      setCountry("Slovensko");
    }
    if (data.dic) setDic(String(data.dic));
    if (data.ic_dph) {
      const v = normalizeIcDphValue(String(data.ic_dph));
      if (v) setIcDph(v);
    }
  }, []);

  useEffect(() => {
    if (!chcemNaFirmu || !embedToken) return;
    const digits = ico.replace(/\D/g, "");
    if (digits.length < 8) {
      setFinstatLookupSuccess(false);
      return;
    }
    const timer = window.setTimeout(async () => {
      setAutoLookupBusy(true);
      setCompanyLoadError(null);
      try {
        const url = apiUrl(
          `/api/public/finstat-lookup?ico=${encodeURIComponent(ico.trim())}&token=${encodeURIComponent(embedToken)}`
        );
        const res = await fetch(url);
        const data = await res.json().catch(() => ({}));
        if (data.success) {
          applyFinstatPayload(data);
          const active = data.is_active !== false;
          setFinstatLookupSuccess(active);
          if (!active) {
            setCompanyLoadError(
              t(
                "configurator.companyInactiveByIco",
                "Firma bola nájdená, ale podľa registra nie je aktívna. Údaje sú predvyplnené, 0% DPH (prenesenie) sa neuplatní."
              )
            );
          }
        } else {
          setFinstatLookupSuccess(false);
        }
      } catch {
        setFinstatLookupSuccess(false);
      } finally {
        setAutoLookupBusy(false);
      }
    }, 550);
    return () => clearTimeout(timer);
  }, [ico, chcemNaFirmu, embedToken, applyFinstatPayload, t]);

  const handleLoadCompany = async () => {
    const trimmed = ico.trim();
    if (!trimmed) return;
    setCompanyLoadError(null);
    setLoadingCompany(true);
    setFinstatLookupSuccess(false);
    try {
      if (embedToken) {
        const url = apiUrl(
          `/api/public/finstat-lookup?ico=${encodeURIComponent(trimmed)}&token=${encodeURIComponent(embedToken)}`
        );
        const res = await fetch(url);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setCompanyLoadError(
            data.message || data.error || t("configurator.companyLoadHttpError", "Nepodarilo sa načítať údaje.")
          );
          return;
        }
        if (data.success) {
          applyFinstatPayload(data);
          const active = data.is_active !== false;
          setFinstatLookupSuccess(active);
          if (!active) {
            setCompanyLoadError(
              t(
                "configurator.companyInactiveByIco",
                "Firma bola nájdená, ale podľa registra nie je aktívna. Údaje sú predvyplnené, 0% DPH (prenesenie) sa neuplatní."
              )
            );
          }
        } else {
          setCompanyLoadError(t("configurator.companyNotFoundByIco", "Firma s týmto IČO sa nenašla."));
        }
      } else {
        const url = apiUrl(`/api/public/company-by-ico?ico=${encodeURIComponent(trimmed)}`);
        const res = await fetch(url);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setCompanyLoadError(data.message || data.error || "IČO neplatné");
          return;
        }
        if (data.valid) {
          if (data.icDph) {
            const v = normalizeIcDphValue(String(data.icDph));
            if (v) setIcDph(v);
          }
          setFinstatLookupSuccess(false);
        } else {
          setCompanyLoadError(data.message || "IČO neplatné");
        }
      }
    } catch {
      setCompanyLoadError(t("configurator.companyLoadNetworkError", "Načítanie zlyhalo."));
    } finally {
      setLoadingCompany(false);
    }
  };

  const isValidEmailFormat = (value: string): boolean => {
    if (!value || typeof value !== "string") return false;
    const s = value.trim();
    if (s.length < 5 || !s.includes("@")) return false;
    return /^[^\s@]+@[^\s@]+(\.[^\s@]+)+$/.test(s);
  };

  const validateEmail = (value: string) => {
    if (!requireContactFields) return;
    if (!value.trim()) {
      setEmailError(null);
      return;
    }
    setEmailError(isValidEmailFormat(value) ? null : "invalid");
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    validateEmail(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setEmail(v);
    setFormError(null);
    if (emailTouched) validateEmail(v);
  };

  /** Returns a translated error message when submit/calculate should be blocked. */
  const getSubmitValidationError = (): string | null => {
    const w = Number(width);
    const h = Number(height);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
      return t("configurator.errorDimensions");
    }
    if (requireContactFields) {
      if (email.trim() && !isValidEmailFormat(email)) {
        return t("configurator.errorInvalidEmail");
      }
      if (!email.trim() || !name.trim() || !phone.trim()) {
        return t("configurator.errorContactRequired");
      }
      if (!chcemNaFirmu) {
        if (!city.trim()) return t("configurator.errorContactRequired");
      } else if (!finstatLookupSuccess || !obchodneMeno.trim() || !city.trim() || !country.trim()) {
        return t("configurator.errorCompanyRequired");
      }
    }
    if ((onCalculate || onSubmitToPublic) && !gdprConsent) {
      return t("configurator.errorGdpr");
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (requireContactFields) {
      setEmailTouched(true);
      if (email.trim() && !isValidEmailFormat(email)) {
        setEmailError("invalid");
      }
    }
    const validationError = getSubmitValidationError();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const values = buildConfiguratorFormValues({
      width,
      height,
      motiv,
      surface,
      color,
      electricDrive,
      address,
      name,
      email,
      phone,
      city,
      country,
      message,
      gdprConsent,
      chcemNaFirmu,
      finstatLookupSuccess,
      ico,
      dic,
      icDph,
      obchodneMeno,
    });
    if (!values) {
      setFormError(t("configurator.errorDimensions"));
      return;
    }

    if (onCalculate) {
      setLoading(true);
      try {
        await onCalculate(values);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRecordAiClassificationClick = async () => {
    if (!onRecordAiClassification || !gdprConsent) return;
    const values = buildConfiguratorFormValues({
      width,
      height,
      motiv,
      surface,
      color,
      electricDrive,
      address,
      name,
      email,
      phone,
      city,
      country,
      message,
      gdprConsent,
      chcemNaFirmu,
      finstatLookupSuccess,
      ico,
      dic,
      icDph,
      obchodneMeno,
    });
    if (!values) return;
    setRecordingAi(true);
    try {
      await onRecordAiClassification(values);
    } finally {
      setRecordingAi(false);
    }
  };

  const handleEmbedSubmit = async (token: string) => {
    if (!onSubmitToPublic) return;
    setFormError(null);
    if (requireContactFields) {
      setEmailTouched(true);
      if (email.trim() && !isValidEmailFormat(email)) {
        setEmailError("invalid");
      }
    }
    const validationError = getSubmitValidationError();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    const w = Number(width);
    const h = Number(height);
    const values: ConfiguratorFormValues = {
      width: w,
      height: h,
      motiv,
      surface,
      color: color.trim(),
      electricDrive,
      address: address.trim(),
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      city: city.trim(),
      country: country.trim(),
      message: message.trim(),
      gdprConsent,
      chcemNaFirmu,
      finstatLookupSuccess,
      ...(chcemNaFirmu && ico.trim()
        ? {
            ico: ico.trim(),
            dic: dic.trim() || undefined,
            icDph: icDph.trim() || undefined,
            ...(obchodneMeno.trim() ? { obchodneMeno: obchodneMeno.trim() } : {}),
          }
        : {}),
    };
    setLoading(true);
    try {
      await onSubmitToPublic(values, token);
    } finally {
      setLoading(false);
    }
  };

  const colorSelectOptions =
    colorOptions.length > 0
      ? colorOptions.map((c) => ({ value: c.code, label: c.name }))
      : [];

  useEffect(() => {
    if (configuratorProductOptions.length > 0) {
      const hasCurrent = configuratorProductOptions.some((o) => o.surface === surface);
      if (!hasCurrent) {
        setSurfaceValue(configuratorProductOptions[0].surface);
      }
    }
  }, [configuratorProductOptions, surface, setSurfaceValue]);

  const selectedMotivLabel = motivOptions.find((o) => o.value === motiv)?.label ?? motiv.toUpperCase();
  const selectedSurfaceLabel =
    surfaceOptions.find((o) => o.value === surface)?.label ?? surface;
  const selectedColorObj = currentOption?.colors.find((c) => c.code === color);
  const selectedColorLabel = selectedColorObj
    ? `${selectedColorObj.code}, ${selectedColorObj.name}`
    : color;

  const getSurfaceThumbSrc = (surfaceValue: string): { src: string | null; hex: string } => {
    const opt = configuratorProductOptions.find((o) => o.surface === surfaceValue);
    const firstColor = opt?.colors[0];
    if (!firstColor) return { src: null, hex: DEFAULT_SWATCH_PLACEHOLDER_HEX };
    const hex = firstColor.colorHex ?? DEFAULT_SWATCH_PLACEHOLDER_HEX;
    if (firstColor.overlayFilename && !/[/\\]/.test(firstColor.overlayFilename)) {
      return { src: `/configurator/overlays/${firstColor.overlayFilename}`, hex };
    }
    const s = normalizeForPath(surfaceValue);
    const c = normalizeColorCodeForPath(firstColor.code);
    if (!s || !c) return { src: null, hex };
    return { src: `/configurator/overlays/${s}_${c}.jpg`, hex };
  };

  return (
    <form onSubmit={handleSubmit} className="configurator-main-form space-y-4 pt-6">
      {/* --- Šírka / Výška --- */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="config-width" className="text-sm font-medium">
            {t("configurator.widthShort", "Šírka")}
            <span className="configurator-asterisk">*</span>
          </Label>
          <div className="input-mm-wrapper">
            <Input
              id="config-width"
              type="number"
              step="1"
              autoComplete="off"
              value={width}
              onChange={(e) => {
                setWidth(e.target.value);
                setFormError(null);
              }}
              placeholder={t("configurator.placeholderWidth")}
              required
              className="h-12 text-base"
            />
            <span className="input-mm-suffix">mm</span>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="config-height" className="text-sm font-medium">
            {t("configurator.heightShort", "Výška")}
            <span className="configurator-asterisk">*</span>
          </Label>
          <div className="input-mm-wrapper">
            <Input
              id="config-height"
              type="number"
              step="1"
              autoComplete="off"
              value={height}
              onChange={(e) => {
                setHeight(e.target.value);
                setFormError(null);
              }}
              placeholder={t("configurator.placeholderHeight")}
              required
              className="h-12 text-base"
            />
            <span className="input-mm-suffix">mm</span>
          </div>
        </div>
      </div>

      {/* --- Motív brány --- */}
      <div className="space-y-1.5 configurator-section-animate">
        <div className="configurator-section-label">
          <span className="text-sm font-medium">{t("configurator.gateMotif", "Motív brány")}</span>
          <span className="configurator-label-separator"> – </span>
          <span className="selected-value">Prelis {selectedMotivLabel}</span>
          <span className="configurator-asterisk">*</span>
        </div>
        <div className="grid grid-cols-[100px_100px_100px] gap-3">
          <AnimatePresence mode="popLayout">
            {motivOptions.map((o) => (
              <motion.div
                key={o.value}
                layout
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                className="flex flex-col items-center"
              >
                <MotivThumbnail
                  value={o.value}
                  selected={motiv === o.value}
                  onSelect={() => setMotivValue(o.value)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* --- Povrch (surface thumbnails, nem pill buttons) --- */}
      <div className="space-y-1.5 configurator-section-animate">
        <div className="configurator-section-label">
          <span className="text-sm font-medium">{t("configurator.surfaceShort", "Povrch")}</span>
          <span className="configurator-label-separator"> – </span>
          <span className="selected-value">{selectedSurfaceLabel}</span>
          <span className="configurator-asterisk">*</span>
        </div>
        <div className="grid grid-cols-[100px_100px_100px] gap-3">
          <AnimatePresence mode="popLayout">
            {surfaceOptions.map((o) => {
              const thumb = getSurfaceThumbSrc(o.value);
              return (
                <motion.div
                  key={o.value}
                  layout
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                  className="flex flex-col items-center"
                >
                  <SurfaceThumbnail
                    overlaySrc={thumb.src}
                    fallbackHex={thumb.hex}
                    selected={surface === o.value}
                    onSelect={() => setSurfaceValue(o.value)}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* --- Farba / szín (3 oszlopos grid) --- */}
      <div className="space-y-1.5 configurator-section-animate">
        <div className="configurator-section-label">
          <span className="text-sm font-medium">{t("configurator.colorShort", "Farba")}</span>
          {selectedColorLabel && (
            <>
              <span className="configurator-label-separator"> – </span>
              <span className="selected-value">{selectedColorLabel}</span>
            </>
          )}
          <span className="configurator-asterisk">*</span>
        </div>
        {colorSelectOptions.length > 0 ? (
          <div className="grid grid-cols-[100px_100px_100px] gap-3">
            <AnimatePresence mode="popLayout">
              {colorSelectOptions.map((c) => {
                const colorObj = currentOption?.colors.find((x) => x.code === c.value);
                const hex = colorObj?.colorHex ?? DEFAULT_SWATCH_PLACEHOLDER_HEX;
                const overlayPath =
                  surface?.trim() && c.value?.trim()
                    ? colorObj?.overlayFilename && !/[/\\]/.test(colorObj.overlayFilename)
                      ? `/configurator/overlays/${colorObj.overlayFilename}`
                      : `/configurator/overlays/${normalizeForPath(surface)}_${normalizeColorCodeForPath(c.value)}.jpg`
                    : null;
                return (
                  <motion.div
                    key={c.value}
                    layout
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                    className="flex flex-col items-center"
                  >
                    <button
                      type="button"
                      onClick={() => setColorValue(c.value)}
                      title={c.label}
                      className={`configurator-swatch relative shadow-sm ${
                        color === c.value ? "selected" : ""
                      }`}
                      style={{ width: 100, height: 75 }}
                    >
                      <span className="swatch-content">
                        <ColorSwatch overlaySrc={overlayPath} fallbackHex={hex} />
                      </span>
                      {color === c.value && <CheckBadge overlay />}
                    </button>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : (
          <Input
            value={color}
            onChange={(e) => setColorValue(e.target.value)}
            placeholder={t("configurator.placeholderRal")}
          />
        )}
      </div>

      {/* --- Elektrický pohon --- */}
      <div className="flex items-center gap-2.5 py-2">
        <Checkbox
          id="config-electric"
          className="configurator-electric-checkbox"
          checked={electricDrive}
          onCheckedChange={(c) => setElectricDrive(!!c)}
        />
        <Label htmlFor="config-electric" className="text-sm font-medium">
          {t("configurator.electricDrive")}
        </Label>
      </div>

      {/* --- Kontaktné údaje (magán: 1 kártya; cég: Firemné + Kontaktná osoba) --- */}
      <section className="space-y-4 pt-6 border-t border-border">
        {!showCompanyBlock ? (
          <div className={contactCardClass}>
            <h3 className="configurator-card-section-title text-xs font-medium uppercase tracking-[0.12em]">
              {t("configurator.contactSection")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="config-name" className="text-sm font-bold">
                  {t("configurator.contactPersonName")}
                </Label>
                <Input
                  id="config-name"
                  value={name}
                  onChange={(e) => {
                        setName(e.target.value);
                        setFormError(null);
                      }}
                  placeholder={t("configurator.placeholderContactPerson")}
                  className="h-12 text-base"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="config-email" className="text-sm font-bold">
                  {t("configurator.email")}
                </Label>
                <Input
                  id="config-email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  placeholder={t("configurator.placeholderEmail")}
                  className={cn("h-12 text-base", emailError && "border-destructive")}
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? "config-email-error" : undefined}
                />
                {emailError && (
                  <p id="config-email-error" className="text-sm text-destructive" role="alert">
                    {t("configurator.errorInvalidEmail")}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="config-phone" className="text-sm font-bold">
                  {t("configurator.phone")}
                </Label>
                <Input
                  id="config-phone"
                  value={phone}
                  onChange={(e) => {
                        setPhone(e.target.value);
                        setFormError(null);
                      }}
                  placeholder={t("configurator.placeholderPhone")}
                  className="h-12 text-base"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="config-city" className="text-sm font-bold">
                  {t("configurator.city")}
                </Label>
                <Input
                  id="config-city"
                  value={city}
                  onChange={(e) => {
                        setCity(e.target.value);
                        setFormError(null);
                      }}
                  placeholder={t("configurator.placeholderCity")}
                  className="h-12 text-base"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="config-address" className="text-sm font-bold">
                  {t("configurator.addressShort", "Adresa")}
                </Label>
                <Input
                  id="config-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={t("configurator.placeholderAddress")}
                  className="h-12 text-base"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="config-country" className="text-sm font-bold">
                  {t("configurator.country")}
                </Label>
                <select
                  id="config-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className={CONFIGURATOR_SELECT_FIELD_CLASS}
                >
                  <option value="Slovensko">{t("configurator.countrySlovakia")}</option>
                  <option value="Maďarsko">{t("configurator.countryHungary")}</option>
                  <option value="Rakúsko">{t("configurator.countryAustria")}</option>
                </select>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="config-message" className="text-sm font-bold">
                  {t("configurator.message")}
                </Label>
                <textarea
                  id="config-message"
                  className={CONFIGURATOR_TEXTAREA_FIELD_CLASS}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("configurator.messagePlaceholder")}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2.5 pt-1">
              <Checkbox
                id="config-chcem-firmu"
                checked={chcemNaFirmu}
                onCheckedChange={(v) => {
                  const on = !!v;
                  setChcemNaFirmu(on);
                  if (!on) {
                    setIco("");
                    setObchodneMeno("");
                    setDic("");
                    setIcDph("");
                    setFinstatLookupSuccess(false);
                    setCompanyLoadError(null);
                    setAddress(defaultValues.address);
                    setCity(defaultValues.city);
                    setCountry(defaultValues.country);
                  }
                }}
              />
              <Label htmlFor="config-chcem-firmu" className="text-sm font-medium">
                {t("configurator.chcemNaFirmu")}
              </Label>
            </div>

            {!chcemNaFirmu ? (
              <div className={contactCardClass}>
                <h3 className="configurator-card-section-title text-xs font-medium uppercase tracking-[0.12em]">
                  {t("configurator.contactSection")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="config-name" className="text-sm font-bold">
                      {t("configurator.contactPersonName")}{" "}
                      {requireContactFields && <span className="configurator-asterisk">*</span>}
                    </Label>
                    <Input
                      id="config-name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setFormError(null);
                      }}
                      placeholder={t("configurator.placeholderContactPerson")}
                      required={requireContactFields}
                      className="h-12 text-base"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="config-email" className="text-sm font-bold">
                      {t("configurator.email")}{" "}
                      {requireContactFields && <span className="configurator-asterisk">*</span>}
                    </Label>
                    <Input
                      id="config-email"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      onBlur={handleEmailBlur}
                      placeholder={t("configurator.placeholderEmail")}
                      required={requireContactFields}
                      className={cn("h-12 text-base", emailError && "border-destructive")}
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? "config-email-error" : undefined}
                    />
                    {emailError && (
                      <p id="config-email-error" className="text-sm text-destructive" role="alert">
                        {t("configurator.errorInvalidEmail")}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="config-phone" className="text-sm font-bold">
                      {t("configurator.phone")}{" "}
                      {requireContactFields && <span className="configurator-asterisk">*</span>}
                    </Label>
                    <Input
                      id="config-phone"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        setFormError(null);
                      }}
                      placeholder={t("configurator.placeholderPhone")}
                      required={requireContactFields}
                      className="h-12 text-base"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="config-city" className="text-sm font-bold">
                      {t("configurator.city")}{" "}
                      {requireContactFields && <span className="configurator-asterisk">*</span>}
                    </Label>
                    <Input
                      id="config-city"
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        setFormError(null);
                      }}
                      placeholder={t("configurator.placeholderCity")}
                      required={requireContactFields}
                      className="h-12 text-base"
                    />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label htmlFor="config-address" className="text-sm font-bold">
                      {t("configurator.addressShort", "Adresa")}
                    </Label>
                    <Input
                      id="config-address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder={t("configurator.placeholderAddress")}
                      className="h-12 text-base"
                    />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label htmlFor="config-country" className="text-sm font-bold">
                      {t("configurator.country")}
                    </Label>
                    <select
                      id="config-country"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className={CONFIGURATOR_SELECT_FIELD_CLASS}
                    >
                      <option value="Slovensko">{t("configurator.countrySlovakia")}</option>
                      <option value="Maďarsko">{t("configurator.countryHungary")}</option>
                      <option value="Rakúsko">{t("configurator.countryAustria")}</option>
                    </select>
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label htmlFor="config-message" className="text-sm font-bold">
                      {t("configurator.message")}
                    </Label>
                    <textarea
                      id="config-message"
                      className={CONFIGURATOR_TEXTAREA_FIELD_CLASS}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t("configurator.messagePlaceholder")}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className={contactCardClass}>
                  <h3 className="configurator-card-section-title text-xs font-medium uppercase tracking-[0.12em]">
                    {t("configurator.firemneUdaje")}
                  </h3>
                  <div className="flex flex-col gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="config-ico" className="text-sm font-bold">
                        IČO
                      </Label>
                      <Input
                        id="config-ico"
                        value={ico}
                        onChange={(e) => {
                          const v = e.target.value;
                          setIco(v);
                          setCompanyLoadError(null);
                          if (finstatLookupSuccess) {
                            setFinstatLookupSuccess(false);
                            setObchodneMeno("");
                            setDic("");
                            setIcDph("");
                            setAddress("");
                            setCity("");
                            setCountry(defaultValues.country);
                          }
                        }}
                        placeholder={t("configurator.placeholderIco")}
                        className="h-12 text-base"
                        inputMode="numeric"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleLoadCompany}
                      disabled={!ico.trim() || loadingCompany || autoLookupBusy}
                      className="configurator-btn-primary w-full py-6 text-base font-semibold"
                    >
                      {loadingCompany ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          {t("common.loading")}
                        </span>
                      ) : (
                        t("configurator.loadCompanyData")
                      )}
                    </Button>
                    {autoLookupBusy && (
                      <p className="text-xs text-muted-foreground text-center">
                        {t("configurator.checkingIco", "Overujem IČO…")}
                      </p>
                    )}
                  </div>
                  {finstatLookupSuccess && (
                    <div
                      className="flex items-start gap-2.5 rounded-md border border-emerald-600/35 bg-emerald-500/[0.12] px-3 py-2.5 text-sm text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-100"
                      role="status"
                    >
                      <CheckCircle2
                        className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5"
                        aria-hidden
                      />
                      <span className="leading-snug">{t("configurator.companyVatExemptBanner")}</span>
                    </div>
                  )}
                  {showCompanyDetailFields && (
                    <div className="space-y-3 pt-1">
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="config-obchodne-meno"
                          className={cn("text-sm font-bold", companyFieldsLocked && "configurator-label-subtle")}
                        >
                          {t("configurator.obchodneMeno")}
                        </Label>
                        <Input
                          id="config-obchodne-meno"
                          value={obchodneMeno}
                          onChange={(e) => setObchodneMeno(e.target.value)}
                          placeholder={t("configurator.placeholderObchodneMeno")}
                          disabled={companyFieldsLocked}
                          className={cn("h-12 text-base", companyFieldsLocked && finstatLockedInputClass)}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="config-dic"
                            className={cn("text-sm font-medium", companyFieldsLocked && "configurator-label-subtle")}
                          >
                            {t("configurator.labelDic")}
                          </Label>
                          <Input
                            id="config-dic"
                            value={dic}
                            onChange={(e) => setDic(e.target.value)}
                            disabled={companyFieldsLocked}
                            className={cn("h-11 text-base", companyFieldsLocked && finstatLockedInputClass)}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="config-icdph"
                            className={cn("text-sm font-medium", companyFieldsLocked && "configurator-label-subtle")}
                          >
                            {t("configurator.labelIcDph")}
                          </Label>
                          <Input
                            id="config-icdph"
                            value={icDph}
                            onChange={(e) => setIcDph(e.target.value)}
                            disabled={companyFieldsLocked}
                            className={cn("h-11 text-base", companyFieldsLocked && finstatLockedInputClass)}
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="config-address"
                          className={cn("text-sm font-bold", companyFieldsLocked && "configurator-label-subtle")}
                        >
                          {t("configurator.addressShort", "Adresa")}
                        </Label>
                        <Input
                          id="config-address"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          placeholder={t("configurator.placeholderAddress")}
                          disabled={companyFieldsLocked}
                          className={cn("h-12 text-base", companyFieldsLocked && finstatLockedInputClass)}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="config-city"
                            className={cn("text-sm font-bold", companyFieldsLocked && "configurator-label-subtle")}
                          >
                            {t("configurator.city")}
                          </Label>
                          <Input
                            id="config-city"
                            value={city}
                            onChange={(e) => {
                        setCity(e.target.value);
                        setFormError(null);
                      }}
                            placeholder={t("configurator.placeholderCity")}
                            disabled={companyFieldsLocked}
                            className={cn("h-12 text-base", companyFieldsLocked && finstatLockedInputClass)}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="config-country"
                            className={cn("text-sm font-bold", companyFieldsLocked && "configurator-label-subtle")}
                          >
                            {t("configurator.country")}
                          </Label>
                          <select
                            id="config-country"
                            value={country}
                            disabled={companyFieldsLocked}
                            onChange={(e) => setCountry(e.target.value)}
                            className={cn(CONFIGURATOR_SELECT_FIELD_CLASS, companyFieldsLocked && finstatLockedInputClass)}
                          >
                            <option value="Slovensko">{t("configurator.countrySlovakia")}</option>
                            <option value="Maďarsko">{t("configurator.countryHungary")}</option>
                            <option value="Rakúsko">{t("configurator.countryAustria")}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                  {companyLoadError && <span className="text-sm text-destructive">{companyLoadError}</span>}
                </div>

                <div className={contactCardClass}>
                  <h3 className="configurator-card-section-title text-xs font-medium uppercase tracking-[0.12em]">
                    {t("configurator.kontaktnaOsoba")}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="config-name" className="text-sm font-bold">
                        {t("configurator.contactPersonName")}{" "}
                        {requireContactFields && <span className="configurator-asterisk">*</span>}
                      </Label>
                      <Input
                        id="config-name"
                        value={name}
                        onChange={(e) => {
                        setName(e.target.value);
                        setFormError(null);
                      }}
                        placeholder={t("configurator.placeholderContactPerson")}
                        required={requireContactFields}
                        className="h-12 text-base"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="config-email" className="text-sm font-bold">
                        {t("configurator.email")}{" "}
                        {requireContactFields && <span className="configurator-asterisk">*</span>}
                      </Label>
                      <Input
                        id="config-email"
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        onBlur={handleEmailBlur}
                        placeholder={t("configurator.placeholderEmail")}
                        required={requireContactFields}
                        className={cn("h-12 text-base", emailError && "border-destructive")}
                        aria-invalid={!!emailError}
                        aria-describedby={emailError ? "config-email-error-co" : undefined}
                      />
                      {emailError && (
                        <p id="config-email-error-co" className="text-sm text-destructive" role="alert">
                          {t("configurator.errorInvalidEmail")}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                      <Label htmlFor="config-phone" className="text-sm font-bold">
                        {t("configurator.phone")}{" "}
                        {requireContactFields && <span className="configurator-asterisk">*</span>}
                      </Label>
                      <Input
                        id="config-phone"
                        value={phone}
                        onChange={(e) => {
                        setPhone(e.target.value);
                        setFormError(null);
                      }}
                        placeholder={t("configurator.placeholderPhone")}
                        required={requireContactFields}
                        className="h-12 text-base"
                      />
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                      <Label htmlFor="config-message" className="text-sm font-bold">
                        {t("configurator.message")}
                      </Label>
                      <textarea
                        id="config-message"
                        className={CONFIGURATOR_TEXTAREA_FIELD_CLASS}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t("configurator.messagePlaceholder")}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </section>

      {(onCalculate || onSubmitToPublic) && (
        <div className="flex items-center gap-2.5">
          <Checkbox
            id="config-gdpr"
            checked={gdprConsent}
            onCheckedChange={(c) => {
              setGdprConsent(!!c);
              setFormError(null);
            }}
          />
          <Label htmlFor="config-gdpr" className="configurator-gdpr-label text-sm font-light leading-snug">
            {t("configurator.gdprConsent")}
          </Label>
        </div>
      )}

      {formError && (
        <p className="text-sm text-destructive" role="alert">
          {formError}
        </p>
      )}

      {onCalculate && (
        <Button
          type="submit"
          disabled={loading || recordingAi || !gdprConsent}
          className="configurator-btn-primary w-full py-6 text-base font-semibold"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              {t("configurator.calculate")}
            </span>
          ) : (
            t("configurator.calculate")
          )}
        </Button>
      )}
      {onCalculate && onRecordAiClassification && (
        <Button
          type="button"
          variant="outline"
          disabled={loading || recordingAi || !gdprConsent}
          className="w-full py-5 text-sm font-medium border-dashed"
          onClick={() => void handleRecordAiClassificationClick()}
        >
          {recordingAi ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              {t("priceGenerator.testerAiClassifyRunning")}
            </span>
          ) : (
            t("priceGenerator.testerAiClassifyButton")
          )}
        </Button>
      )}
      {onSubmitToPublic && (
        <Button
          type="button"
          disabled={loading || !gdprConsent}
          className="configurator-btn-primary w-full py-6 text-base font-semibold"
          onClick={() => {
            const token =
              embedToken?.trim() ||
              (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("token")?.trim() : null);
            if (!token) {
              setFormError(t("configurator.errorMissingToken"));
              return;
            }
            void handleEmbedSubmit(token);
          }}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              {t("configurator.submit")}
            </span>
          ) : (
            t("configurator.submit")
          )}
        </Button>
      )}

      {/* safe-area: top inset */}
      <AnimatePresence>
        {loading && onSubmitToPublic && (
          <motion.div
            key="embed-loading-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm pt-[env(safe-area-inset-top)] px-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]"
            role="status"
            aria-busy="true"
          >
            <div className="flex flex-col items-center gap-4 rounded-xl bg-background p-8 shadow-xl border border-border/60">
              <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-[hsl(var(--configurator-accent))] border-t-transparent" />
              <p className="text-base font-semibold text-foreground">
                {t("configurator.embedSubmitGenerating", "Generujeme vašu cenovú ponuku…")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("configurator.embedSubmitPleaseWait", "Prosím čakajte, môže to trvať niekoľko sekúnd.")}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  );
}
